import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Check, Copy, KeyRound, Trash2 } from "lucide-react"
import { useState } from "react"

import { Feature } from "@/components/feature"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem, SharedProps } from "@/types"

interface ApiTokenRow {
  id: number
  name: string
  token_prefix: string
  scopes: string[]
  last_used_at: string | null
  created_at: string
}

interface Props {
  tokens: ApiTokenRow[]
  new_token: string | null
}

const breadcrumbs: BreadcrumbItem[] = [{ title: "API tokens", href: "#" }]

export default function ApiTokens({ tokens, new_token }: Props) {
  const org = usePage<SharedProps>().props.organization
  const slug = org?.slug ?? ""
  const routes = useOrgRoutes()
  const { data, setData, post, processing, reset } = useForm({ name: "" })
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // The one-time secret arrives as the `new_token` prop right after create.
  // Derive the reveal from props (no effect) and remember the value the user
  // dismissed, so the dialog re-opens when a *different* token is created.
  const revealed = new_token && new_token !== dismissed ? new_token : null

  function create(e: React.FormEvent) {
    e.preventDefault()
    post(routes.apiTokensPath(), { onSuccess: () => reset("name") })
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const curlExample = revealed
    ? `curl -H "Authorization: Bearer ${revealed}" \\\n  ${window.location.origin}/api/v1/${slug}/facilities`
    : ""

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="API tokens" />
      <Feature
        name="api_tokens"
        fallback={
          <div className="text-muted-foreground p-6 text-sm">
            This feature isn&apos;t enabled for your organization.
          </div>
        }
      >
        <div className="mx-auto max-w-3xl space-y-6 p-6">
          <div className="space-y-1">
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              <KeyRound className="size-5" /> API tokens
            </h1>
            <p className="text-muted-foreground text-sm">
              Read-only personal access tokens for the API. Scoped to{" "}
              <code className="bg-muted rounded px-1">read:telemetry</code> —
              they can&apos;t change anything.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Create a token</CardTitle>
              <CardDescription>
                Give it a name so you can recognize it later.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={create} className="flex items-end gap-2">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g. Ouster demo laptop"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={processing}>
                  Generate
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Active tokens</CardTitle>
              <CardDescription>{tokens.length} active.</CardDescription>
            </CardHeader>
            <CardContent>
              {tokens.length === 0 ? (
                <p className="text-muted-foreground text-sm">No tokens yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead>Scopes</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokens.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell>
                          <code className="text-xs">{t.token_prefix}…</code>
                        </TableCell>
                        <TableCell>
                          {t.scopes.map((s) => (
                            <Badge key={s} variant="secondary" className="mr-1">
                              {s}
                            </Badge>
                          ))}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {t.last_used_at
                            ? new Date(t.last_used_at).toLocaleString()
                            : "Never"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              router.delete(routes.apiTokenPath(t.id))
                            }
                          >
                            <Trash2 className="size-4" /> Revoke
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        <Dialog
          open={Boolean(revealed)}
          onOpenChange={(o) => {
            if (!o) setDismissed(new_token)
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Copy your new token</DialogTitle>
              <DialogDescription>
                This is the only time you&apos;ll see it. Store it somewhere
                safe.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="bg-muted flex-1 overflow-x-auto rounded p-2 text-xs">
                  {revealed}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => revealed && copy(revealed)}
                >
                  {copied ? (
                    <Check className="size-4" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Try it</Label>
                <pre className="bg-muted overflow-x-auto rounded p-2 text-xs whitespace-pre-wrap">
                  {curlExample}
                </pre>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setDismissed(new_token)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Feature>
    </AppLayout>
  )
}
